import { Page, View, Text } from '@react-pdf/renderer';
import { styles } from './styles';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import { absoluteFilePath } from '@utils/fn';
import { useMemo } from 'react';
import { SafeImage } from '@composites/PDF/AgendaPdfContent/components/SafeImage';

const truncate = (text = '', max = 100) =>
  text.length > max ? `${text.slice(0, max)}...` : text;

export interface CommunityFeedbackPageProps {
  event: PublicEventDetailsResponse;
  footer: JSX.Element;
}

const TextSection = ({
  citation,
  text1,
  text2,
  text3 = '',
}: {
  citation: string;
  text1: string;
  text2: string;
  text3?: string | undefined;
}) => {
  return (
    <View style={styles.textWrapper}>
      <Text style={styles.title}>{citation}</Text>

      <Text style={styles.subtitle}>{text1}</Text>
      <Text style={styles.subtitle}>{text2}</Text>
      {text3 && <Text style={styles.subtitle}>{text3}</Text>}
    </View>
  );
};

const CommunityFeedbackPage = ({
  event,
  footer,
}: CommunityFeedbackPageProps) => {
  const { t, i18n } = useTranslation();

  const testimonialsItems = useMemo(
    () =>
      event?.website_content?.testimonials
        ?.filter(
          e =>
            e.is_home &&
            e?.first_name &&
            e?.last_name &&
            e?.citation &&
            e?.company_name
        )
        ?.sort((a, b) => a.order - b.order)
        ?.map((e, id) => ({
          id: String(id),
          name: [e?.first_name, e?.last_name]?.filter(e => e).join(' '),
          jobTitle: e?.job_title,
          companyName: e?.company_name,
          videoLink: e?.video_url,
          logoLink: absoluteFilePath(e?.company_logo),
          citation: e?.citation,
        }))
        ?.slice(0, 7),
    [event?.website_content?.testimonials]
  );

  const items: Array<{
    citation: string;
    jobTitle: string;
    companyName: string;
  }> = [];
  [0, 1, 2, 3, 4, 5, 6].forEach((item, index) => {
    const newItem = {
      citation: truncate(
        testimonialsItems?.[index]?.citation ||
          t(`community-feedback-item-${index + 1}-citation`)
      ),
      jobTitle:
        testimonialsItems?.[index]?.jobTitle ||
        t(`community-feedback-item-${index + 1}-text1`),
      companyName:
        testimonialsItems?.[index]?.companyName ||
        t(`community-feedback-item-${index + 1}-text2`),
    };
    items.push(newItem);
  });

  return (
    <Page
      size={{ width: 1440, height: 800 }}
      style={{
        ...styles.page,
        backgroundColor: event?.design?.themeColor,
        color: 'white',
      }}
    >
      <View
        style={{
          ...styles.columnContainer,
        }}
      >
        <View style={styles.leftColumn}>
          <SafeImage
            src={
              'https://media.we-conect.com/images/pdf/partnerprospectus/10_1.png'
            }
            style={styles.leftBigImage}
          />

          <TextSection
            citation={items?.[0]?.citation}
            text1={items?.[0]?.jobTitle}
            text2={items?.[0]?.companyName}
          />

          <SafeImage
            src={
              'https://media.we-conect.com/images/pdf/partnerprospectus/10_5.png'
            }
            style={styles.leftSmallImage}
          />
        </View>

        <View style={styles.middleColumn}>
          <View style={styles.middleColumnRow1}>
            <TextSection
              citation={items?.[1]?.citation}
              text1={items?.[1]?.jobTitle}
              text2={items?.[1]?.companyName}
            />

            <TextSection
              citation={items?.[2]?.citation}
              text1={items?.[2]?.jobTitle}
              text2={items?.[2]?.companyName}
            />
          </View>

          <SafeImage
            src={
              'https://media.we-conect.com/images/pdf/partnerprospectus/10_2.png'
            }
            style={styles.middleColumnBigImage}
          />

          <View style={styles.middleColumnRow1}>
            <TextSection
              citation={items?.[3]?.citation}
              text1={items?.[3]?.jobTitle}
              text2={items?.[3]?.companyName}
            />

            <SafeImage
              src={
                'https://media.we-conect.com/images/pdf/partnerprospectus/10_6.png'
              }
              style={styles.leftSmallImage}
            />
          </View>
        </View>

        <View style={styles.rightColumn}>
          <View style={styles.rightColumnWrapper}>
            <View style={styles.columnWrapper}>
              <SafeImage
                src={
                  'https://media.we-conect.com/images/pdf/partnerprospectus/10_3.png'
                }
                style={styles.rightColumnLeftImage}
              />

              <TextSection
                citation={items?.[4]?.citation}
                text1={items?.[4]?.jobTitle}
                text2={items?.[4]?.companyName}
              />
              <TextSection
                citation={items?.[5]?.citation}
                text1={items?.[5]?.jobTitle}
                text2={items?.[5]?.companyName}
              />
            </View>

            <View style={styles.columnWrapper}>
              <TextSection
                citation={items?.[6]?.citation}
                text1={items?.[6]?.jobTitle}
                text2={items?.[6]?.companyName}
              />

              <SafeImage
                src={
                  'https://media.we-conect.com/images/pdf/partnerprospectus/10_4.png'
                }
                style={styles.rightColumnRightImage}
              />
            </View>
          </View>
        </View>
      </View>
      {footer}
    </Page>
  );
};

export default CommunityFeedbackPage;
